import { TemplateVariablePanel } from "@components/settings/TemplateVariablePanel";
import { MarkdownEditor } from "@components/shared/MarkdownEditor";
import { CREATE_DOCUMENT_TEMPLATE, UPDATE_DOCUMENT_TEMPLATE } from "@lib/graphql/mutations";
import { DOCUMENT_TEMPLATES_QUERY } from "@lib/graphql/queries";
import { type Connection, usePaginatedList } from "@lib/hooks/usePaginatedList";
import { FileText, Pencil, Plus, X } from "lucide-react";
import { useRef, useState } from "react";
import { useMutation } from "urql";

const TEMPLATE_TYPES = [
  { value: "candidate_report", label: "Candidate Report" },
  { value: "shortlist_report", label: "Shortlist Report" },
  { value: "client_brief", label: "Client Brief" },
  { value: "interview_guide", label: "Interview Guide" },
] as const;

const TYPE_LABELS: Record<string, string> = Object.fromEntries(
  TEMPLATE_TYPES.map((t) => [t.value, t.label]),
);

interface DocumentTemplate {
  id: string;
  name: string;
  templateType: string;
  content: string | null;
  branding: Record<string, unknown> | null;
  isDefault: boolean;
  createdAt: string;
  updatedAt: string;
}

export function DocumentTemplates() {
  const {
    nodes: templates,
    fetching,
    hasNextPage,
    loadMore,
    refresh,
  } = usePaginatedList<{ documentTemplates: Connection<DocumentTemplate> }, DocumentTemplate>({
    query: DOCUMENT_TEMPLATES_QUERY,
    getConnection: (data) => data.documentTemplates,
  });
  const [, createTemplate] = useMutation(CREATE_DOCUMENT_TEMPLATE);
  const [, updateTemplate] = useMutation(UPDATE_DOCUMENT_TEMPLATE);

  const editorRef = useRef<HTMLTextAreaElement>(null);

  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  // Form state
  const [name, setName] = useState("");
  const [templateType, setTemplateType] = useState("candidate_report");
  const [content, setContent] = useState("");
  const [isDefault, setIsDefault] = useState(false);

  function resetForm() {
    setName("");
    setTemplateType("candidate_report");
    setContent("");
    setIsDefault(false);
    setErrors([]);
  }

  function handleInsertVariable(variable: string) {
    const textarea = editorRef.current;
    const insertion = `{{${variable}}}`;
    if (textarea) {
      const start = textarea.selectionStart;
      const end = textarea.selectionEnd;
      const newContent = content.slice(0, start) + insertion + content.slice(end);
      setContent(newContent);
      requestAnimationFrame(() => {
        textarea.focus();
        textarea.selectionStart = textarea.selectionEnd = start + insertion.length;
      });
    } else {
      setContent(content + insertion);
    }
  }

  function openCreate() {
    setEditingId(null);
    resetForm();
    setShowForm(true);
  }

  function openEdit(template: DocumentTemplate) {
    setShowForm(false);
    setEditingId(template.id);
    setName(template.name);
    setTemplateType(template.templateType);
    setContent(template.content || "");
    setIsDefault(template.isDefault);
    setErrors([]);
  }

  function cancelEdit() {
    setEditingId(null);
    resetForm();
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    const result = await createTemplate({
      input: { name, templateType, content: content || null, isDefault },
    });
    const mutationErrors = result.data?.createDocumentTemplate?.errors || [];
    if (mutationErrors.length === 0) {
      setShowForm(false);
      resetForm();
      refresh();
    } else {
      setErrors(mutationErrors);
    }
  }

  async function handleUpdate(e: React.FormEvent) {
    e.preventDefault();
    setErrors([]);
    const result = await updateTemplate({
      input: { id: editingId, name, content: content || null, isDefault },
    });
    const mutationErrors = result.data?.updateDocumentTemplate?.errors || [];
    if (mutationErrors.length === 0) {
      setEditingId(null);
      resetForm();
      refresh();
    } else {
      setErrors(mutationErrors);
    }
  }

  if (fetching && templates.length === 0) {
    return <p className="text-sm text-text-muted">Loading templates...</p>;
  }

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-text-primary">Document Templates</h2>
        <button
          onClick={openCreate}
          className="flex items-center gap-2 rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
        >
          <Plus size={16} /> Add Template
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={handleCreate}
          className="mt-4 rounded-lg bg-card p-4 shadow-sm ring-1 ring-ring"
        >
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold text-text-primary">New Template</h3>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="rounded-md p-1 text-text-faint hover:bg-card-hover"
            >
              <X size={16} />
            </button>
          </div>

          {errors.length > 0 && (
            <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3">
              {errors.map((error) => (
                <p key={error} className="text-sm text-red-700 dark:text-red-400">
                  {error}
                </p>
              ))}
            </div>
          )}

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="block text-sm font-medium text-text-secondary">Name *</label>
              <input
                type="text"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
                placeholder="e.g. Standard Candidate Report"
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-text-secondary">Type *</label>
              <select
                value={templateType}
                onChange={(e) => setTemplateType(e.target.value)}
                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
              >
                {TEMPLATE_TYPES.map((t) => (
                  <option key={t.value} value={t.value}>
                    {t.label}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div className="mt-4">
            <label className="block text-sm font-medium text-text-secondary mb-1">Content</label>
            <TemplateVariablePanel templateType={templateType} onInsert={handleInsertVariable} />
            <div className="mt-2">
              <MarkdownEditor
                ref={editorRef}
                value={content}
                onChange={setContent}
                rows={10}
                placeholder="Template content (markdown)..."
              />
            </div>
          </div>

          <div className="mt-4">
            <label className="flex items-center gap-2 text-sm text-text-secondary">
              <input
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="rounded border-input-border text-accent focus:ring-accent"
              />
              Set as default template for this type
            </label>
          </div>

          <div className="mt-4 flex gap-2">
            <button
              type="submit"
              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
            >
              Create Template
            </button>
            <button
              type="button"
              onClick={() => {
                setShowForm(false);
                resetForm();
              }}
              className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
            >
              Cancel
            </button>
          </div>
        </form>
      )}

      {templates.length === 0 && !showForm ? (
        <div className="mt-3 rounded-lg border-2 border-dashed border-border-dashed p-8 text-center">
          <FileText size={24} className="mx-auto text-text-faint" />
          <p className="mt-2 text-sm text-text-muted">
            No document templates yet. Create one to get started.
          </p>
        </div>
      ) : (
        templates.length > 0 && (
          <div className="mt-3 overflow-hidden rounded-lg bg-card shadow-sm ring-1 ring-ring">
            <table className="min-w-full divide-y divide-border">
              <thead className="bg-card-alt">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Name
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Default
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Updated
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-text-muted">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {templates.map((template) =>
                  editingId === template.id ? (
                    <tr key={template.id}>
                      <td colSpan={5} className="px-4 py-4">
                        <form onSubmit={handleUpdate}>
                          <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-text-primary">
                              Edit Template
                            </h3>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="rounded-md p-1 text-text-faint hover:bg-card-hover"
                            >
                              <X size={16} />
                            </button>
                          </div>

                          {errors.length > 0 && (
                            <div className="mb-4 rounded-md bg-red-50 dark:bg-red-900/20 p-3">
                              {errors.map((error) => (
                                <p key={error} className="text-sm text-red-700 dark:text-red-400">
                                  {error}
                                </p>
                              ))}
                            </div>
                          )}

                          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                            <div>
                              <label className="block text-sm font-medium text-text-secondary">
                                Name *
                              </label>
                              <input
                                type="text"
                                value={name}
                                onChange={(e) => setName(e.target.value)}
                                required
                                className="mt-1 block w-full rounded-md border border-input-border bg-card px-3 py-2 text-sm text-text-primary focus:border-accent focus:outline-none focus:ring-1 focus:ring-accent"
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-text-secondary">
                                Type
                              </label>
                              <p className="mt-1 px-3 py-2 text-sm text-text-muted">
                                {TYPE_LABELS[template.templateType] || template.templateType}
                              </p>
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="block text-sm font-medium text-text-secondary mb-1">
                              Content
                            </label>
                            <TemplateVariablePanel
                              templateType={templateType}
                              onInsert={handleInsertVariable}
                            />
                            <div className="mt-2">
                              <MarkdownEditor
                                ref={editorRef}
                                value={content}
                                onChange={setContent}
                                rows={10}
                                placeholder="Template content (markdown)..."
                              />
                            </div>
                          </div>

                          <div className="mt-4">
                            <label className="flex items-center gap-2 text-sm text-text-secondary">
                              <input
                                type="checkbox"
                                checked={isDefault}
                                onChange={(e) => setIsDefault(e.target.checked)}
                                className="rounded border-input-border text-accent focus:ring-accent"
                              />
                              Set as default template for this type
                            </label>
                          </div>

                          <div className="mt-4 flex gap-2">
                            <button
                              type="submit"
                              className="rounded-md bg-accent px-4 py-2 text-sm font-medium text-white hover:bg-accent-hover"
                            >
                              Save Changes
                            </button>
                            <button
                              type="button"
                              onClick={cancelEdit}
                              className="rounded-md border border-input-border px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover"
                            >
                              Cancel
                            </button>
                          </div>
                        </form>
                      </td>
                    </tr>
                  ) : (
                    <tr key={template.id} className="hover:bg-card-hover">
                      <td className="px-4 py-3 text-sm font-medium text-text-primary">
                        {template.name}
                      </td>
                      <td className="px-4 py-3">
                        <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-xs font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                          {TYPE_LABELS[template.templateType] || template.templateType}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        {template.isDefault && (
                          <span className="inline-flex rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-800 dark:bg-green-900/30 dark:text-green-400">
                            Default
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {new Date(template.updatedAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <button
                          onClick={() => openEdit(template)}
                          className="rounded bg-accent px-3 py-1.5 text-xs font-medium text-white hover:bg-accent/90"
                        >
                          <Pencil size={14} className="inline mr-1" />
                          Edit
                        </button>
                      </td>
                    </tr>
                  ),
                )}
              </tbody>
            </table>
          </div>
        )
      )}

      {hasNextPage && (
        <div className="mt-4 flex justify-center">
          <button
            onClick={loadMore}
            disabled={fetching}
            className="rounded-md border border-input-border bg-card px-4 py-2 text-sm font-medium text-text-secondary hover:bg-card-hover disabled:opacity-50"
          >
            {fetching ? "Loading..." : "Load more"}
          </button>
        </div>
      )}
    </div>
  );
}
